/**
 * Enterprise-grade error reporting service for KernLogic
 * Provides structured error capture, context tracking, and integration-ready monitoring
 */

import { logger, LogContext } from './logger';

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ErrorCategory =
  | 'network'
  | 'validation'
  | 'authentication'
  | 'file_processing'
  | 'import'
  | 'ui'
  | 'system';

export interface ErrorContext extends LogContext {
  userAgent?: string;
  url?: string;
  timestamp?: string;
  stackTrace?: string;
  breadcrumbs?: ErrorBreadcrumb[];
  tags?: Record<string, string>;
  extra?: Record<string, any>;
}

export interface ErrorBreadcrumb {
  timestamp: string;
  category: string;
  message: string;
  level: 'info' | 'warning' | 'error';
  data?: Record<string, any>;
}

export interface CapturedError {
  id: string;
  message: string;
  name: string;
  stack?: string;
  severity: ErrorSeverity;
  category: ErrorCategory;
  context: ErrorContext;
  fingerprint: string;
  timestamp: string;
}

class ErrorReporting {
  private isEnabled: boolean;
  private breadcrumbs: ErrorBreadcrumb[] = [];
  private maxBreadcrumbs = 50;
  private globalContext: ErrorContext = {};

  constructor() {
    this.isEnabled = import.meta.env.PROD || import.meta.env.VITE_ENABLE_ERROR_REPORTING === 'true';
    this.initializeGlobalContext();
    this.setupGlobalErrorHandlers();
  }

  private initializeGlobalContext(): void {
    this.globalContext = {
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      timestamp: new Date().toISOString(),
    };
  }

  private setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') return;

    // Capture unhandled errors
    window.addEventListener('error', event => {
      this.captureException(event.error || new Error(event.message), {
        component: 'GlobalErrorHandler',
        category: 'system',
        severity: 'high',
        extra: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', event => {
      this.captureException(
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        {
          component: 'GlobalErrorHandler',
          category: 'system',
          severity: 'high',
          extra: {
            type: 'unhandledPromiseRejection',
          },
        }
      );
    });
  }

  /**
   * Add a breadcrumb for error context tracking
   */
  addBreadcrumb(breadcrumb: Omit<ErrorBreadcrumb, 'timestamp'>): void {
    const timestampedBreadcrumb: ErrorBreadcrumb = {
      ...breadcrumb,
      timestamp: new Date().toISOString(),
    };

    this.breadcrumbs.push(timestampedBreadcrumb);

    // Keep only the most recent breadcrumbs
    if (this.breadcrumbs.length > this.maxBreadcrumbs) {
      this.breadcrumbs = this.breadcrumbs.slice(-this.maxBreadcrumbs);
    }

    // Log breadcrumb in development
    if (import.meta.env.MODE === 'development') {
      logger.debug(`Breadcrumb: ${breadcrumb.message}`, {
        category: breadcrumb.category,
        level: breadcrumb.level,
        data: breadcrumb.data,
      });
    }
  }

  /**
   * Set global context that will be included with all errors
   */
  setGlobalContext(context: Partial<ErrorContext>): void {
    this.globalContext = { ...this.globalContext, ...context };
  }

  /**
   * Generate a fingerprint for error deduplication
   */
  private generateFingerprint(error: Error, context: ErrorContext): string {
    const parts = [
      error.name,
      error.message,
      context.component || 'unknown',
      context.category || 'unknown',
    ];

    // Create a simple hash from the parts
    const str = parts.join('|');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  }

  /**
   * Capture an exception with full context
   */
  captureException(
    error: Error,
    options: {
      component?: string;
      category?: ErrorCategory;
      severity?: ErrorSeverity;
      tags?: Record<string, string>;
      extra?: Record<string, any>;
      context?: ErrorContext;
    } = {}
  ): string {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const fullContext: ErrorContext = {
      ...this.globalContext,
      ...options.context,
      component: options.component,
      breadcrumbs: [...this.breadcrumbs],
      tags: options.tags,
      extra: options.extra,
      stackTrace: error.stack,
    };

    const capturedError: CapturedError = {
      id: errorId,
      message: error.message,
      name: error.name,
      stack: error.stack,
      severity: options.severity || 'medium',
      category: options.category || 'system',
      context: fullContext,
      fingerprint: this.generateFingerprint(error, fullContext),
      timestamp: new Date().toISOString(),
    };

    // Log the error
    logger.error(`Error captured: ${error.message}`, fullContext, error);

    // Send to external service in production (placeholder for now)
    if (this.isEnabled) {
      this.sendToExternalService(capturedError);
    }

    // Add error as breadcrumb for future context
    this.addBreadcrumb({
      category: 'error',
      level: 'error',
      message: `${error.name}: ${error.message}`,
      data: {
        errorId,
        component: options.component,
        category: options.category,
      },
    });

    return errorId;
  }

  /**
   * Capture a message (non-exception error)
   */
  captureMessage(
    message: string,
    options: {
      component?: string;
      category?: ErrorCategory;
      severity?: ErrorSeverity;
      level?: 'info' | 'warning' | 'error';
      tags?: Record<string, string>;
      extra?: Record<string, any>;
      context?: ErrorContext;
    } = {}
  ): string {
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const fullContext: ErrorContext = {
      ...this.globalContext,
      ...options.context,
      component: options.component,
      breadcrumbs: [...this.breadcrumbs],
      tags: options.tags,
      extra: options.extra,
    };

    // Log based on level
    const level = options.level || 'info';
    switch (level) {
      case 'info':
        logger.info(message, fullContext);
        break;
      case 'warning':
        logger.warn(message, fullContext);
        break;
      case 'error':
        logger.error(message, fullContext);
        break;
    }

    // Add as breadcrumb
    this.addBreadcrumb({
      category: options.category || 'info',
      level: level,
      message,
      data: {
        messageId,
        component: options.component,
      },
    });

    return messageId;
  }

  /**
   * Send error to external monitoring service
   */
  private sendToExternalService(error: CapturedError): void {
    try {
      // Prepare payload for external service (e.g., Sentry, LogRocket, etc.)
      const payload = {
        ...error,
        environment: import.meta.env.MODE,
        release: import.meta.env.VITE_APP_VERSION || 'unknown',
      };

      // Send to external service if endpoint is configured
      if (import.meta.env.VITE_ERROR_REPORTING_ENDPOINT) {
        fetch(import.meta.env.VITE_ERROR_REPORTING_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }).catch(err => {
          // Don't let error reporting errors crash the app
          logger.warn('Failed to send error to external service', {}, err);
        });
      }

      // For now, just log in development
      logger.error(
        'Captured error for external reporting',
        error.context,
        new Error(error.message)
      );
    } catch (err) {
      // Don't let error reporting errors crash the app
      logger.warn('Error in sendToExternalService', {}, err as Error);
    }
  }

  /**
   * Clear breadcrumbs (useful for testing or privacy)
   */
  clearBreadcrumbs(): void {
    this.breadcrumbs = [];
  }

  /**
   * Enable or disable error reporting
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }

  /**
   * Get current breadcrumbs (useful for debugging)
   */
  getBreadcrumbs(): ErrorBreadcrumb[] {
    return [...this.breadcrumbs];
  }
}

// Export singleton instance
export const errorReporting = new ErrorReporting();

// Export helper functions for common use cases
export const captureImportError = (
  error: Error,
  importContext: { importId?: number; importType?: string; component: string }
) => {
  return errorReporting.captureException(error, {
    category: 'import',
    severity: 'high',
    component: importContext.component,
    context: {
      importId: importContext.importId,
      importType: importContext.importType,
    },
  });
};

export const captureNetworkError = (
  error: Error,
  networkContext: { url?: string; method?: string; component: string }
) => {
  return errorReporting.captureException(error, {
    category: 'network',
    severity: 'medium',
    component: networkContext.component,
    context: {
      url: networkContext.url,
      method: networkContext.method,
    },
  });
};

export const captureValidationError = (
  error: Error,
  validationContext: { field?: string; value?: any; component: string }
) => {
  return errorReporting.captureException(error, {
    category: 'validation',
    severity: 'low',
    component: validationContext.component,
    context: {
      field: validationContext.field,
      value: validationContext.value,
    },
  });
};
